import { type ExternalFile, ExternalFileSource } from '@/lib/types';
import { type OneDriveFile } from '../../OneDriveFilePicker/FilePicker/types';
import { LARGE_FILE_THRESHOLD } from '../../utils/upload/LargeFileUploader/AbstractLargeFileUploader';
import useGraphClient from '../../utils/microsoft/useGraphClient';
import OneDriveLargeFileUploader from './OneDriveLargeFileUploader';
import { FALLBACK_MIME_TYPE } from '../../utils/constants';
import useGetOneDriveFilePublicUrl from '../../utils/microsoft/useGetOneDriveFilePublicUrl';
import * as Sentry from '@sentry/react';
import GenericUploadError from '../../utils/upload/GenericUploadError';

const useUploadFileToOneDrive = () => {
  const graphClient = useGraphClient();
  const getOneDriveFilePublicUrl = useGetOneDriveFilePublicUrl();

  const performUpload = async (
    file: File,
    onProgressUpdate: (progress: number) => void,
  ): Promise<OneDriveFile> => {
    if (file.size > LARGE_FILE_THRESHOLD) {
      return new OneDriveLargeFileUploader(graphClient).uploadLargeFile(
        file,
        onProgressUpdate,
      );
    }

    return graphClient
      .api(
        `/me/drive/items/root:/${file.name}:/content?@microsoft.graph.conflictBehavior=rename`,
      )
      .put(file);
  };

  const getOneDriveFilePublicUrlWithFallback = async (
    oneDriveFile: OneDriveFile,
  ): Promise<string> => {
    try {
      const oneDriveFilePublicUrl = await getOneDriveFilePublicUrl(
        oneDriveFile.id,
      );

      return oneDriveFilePublicUrl;
    } catch (error) {
      // no-op: we likely don't have sufficient permissions to get a public
      // URL. Still, we want to keep an eye on these errors for now.
      Sentry.captureException(error);

      if (!oneDriveFile.webUrl) {
        throw new GenericUploadError('Cannot acquire url for uploaded file.');
      }

      return oneDriveFile.webUrl;
    }
  };

  const uploadFileToOneDrive = async (
    file: File,
    onProgressUpdate: (progress: number) => void,
  ): Promise<ExternalFile> => {
    const oneDriveFile = await performUpload(file, onProgressUpdate);

    const oneDriveFilePublicUrl =
      await getOneDriveFilePublicUrlWithFallback(oneDriveFile);

    return {
      source: ExternalFileSource.MICROSOFT_ONEDRIVE,
      externalId: oneDriveFile.id,
      filename: oneDriveFile.name,
      url: oneDriveFilePublicUrl,
      mimeType: oneDriveFile.file?.mimeType ?? FALLBACK_MIME_TYPE,
    };
  };

  return uploadFileToOneDrive;
};

export default useUploadFileToOneDrive;
